import { ExtractComponentProp, FormValues } from '@approvalmax/types';
import { DatePicker, Form, Grid, NumberField, Select, Spacing, Text, TextField } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { constants, selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useRef } from 'react';
import bemFactory from 'react-bem-factory';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
    changeQbooksShippingAddress,
    changeQbooksShippingDate,
    changeQbooksShippingFromAddress,
    changeQbooksShippingMethod,
    changeQbooksShippingPrice,
    changeQbooksShippingTaxCode,
    changeQbooksTrackingNumber,
} from '../../../actions';
import { getRequest } from '../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../selectors/qbooks';
import { getTaxCodeList } from '../../../selectors/qbooks/taxSelectors';
import { messages } from './SalesInvoiceShippingDetailsSection.messages';
import { schema } from './SalesInvoiceShippingDetailsSection.schema';

const qa = bemFactory.qa('reqf-qbooks-sales-shipping-section');

export const SalesInvoiceShippingDetailsSection = memo(() => {
    const dispatch = useDispatch();
    const { details, currency, companyId } = useSelector<domain.QBooksInvoiceRequest>(getRequest);
    const company = useSelector((state) => selectors.company.findCompanyById(state, companyId));
    const qbooksContext = useSelector(getQBooksContext);
    const isInitialShippingPresent = useRef(Object.values(details.shipping).some(Boolean));
    const isShippingEnabled =
        qbooksContext.salesFormOptionalFields?.shippingEnabled || isInitialShippingPresent.current;
    const isUSOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;
    const taxRateItems = getTaxCodeList(qbooksContext, details.date, domain.TaxApplicableOnType.Sales);

    const form = useForm<z.infer<typeof schema>>({
        values: {
            shippingTo: details.shipping.address || '',
            shipVia: details.shipping.method || '',
            trackingNumber: details.shipping.trackingNumber || '',
            shippingDate: details.shipping.date || '',
            shippingFrom: details.shipping.fromAddress || '',
            shippingPrice:
                details.lineAmountType === domain.LineAmountType.TaxInclusive
                    ? details.shipping.priceGross ?? null
                    : details.shipping.price ?? null,
            shippingTaxCode: details.shippingTaxCode?.id || '',
        },
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const handleFormChange: ExtractComponentProp<typeof Form<FormValues<typeof form>>, 'onChange'> = (
        values,
        rootName
    ) => {
        switch (rootName) {
            case 'shippingTo':
                dispatch(changeQbooksShippingAddress(values.shippingTo));
                break;
            case 'shipVia':
                dispatch(changeQbooksShippingMethod(values.shipVia));
                break;
            case 'trackingNumber':
                dispatch(changeQbooksTrackingNumber(values.trackingNumber));
                break;
            case 'shippingDate':
                dispatch(changeQbooksShippingDate(values.shippingDate));
                break;
            case 'shippingFrom':
                dispatch(changeQbooksShippingFromAddress(values.shippingFrom));
                break;
            case 'shippingPrice':
                dispatch(changeQbooksShippingPrice(values.shippingPrice));
                break;
            case 'shippingTaxCode':
                dispatch(
                    changeQbooksShippingTaxCode(taxRateItems.find(({ id }) => id === values.shippingTaxCode) ?? null)
                );
                break;
        }
    };

    if (!isShippingEnabled) {
        return null;
    }

    return (
        <Form form={form} onChange={handleFormChange} data-qa={qa()}>
            <Text font='headline' fontSize='xxsmall' fontWeight='medium' as='h2'>
                {messages.title}
            </Text>

            <Spacing height={16} />

            <Grid gap={16} gridTemplateColumns='2fr 1fr 1fr'>
                <Grid gap={16}>
                    <TextField
                        name='shippingTo'
                        label={messages.shippingToLabel}
                        placeholder={messages.shippingToPlaceholder}
                        multiline
                        fullHeight
                        maxLength={constants.qBooksConstants.SHIPPING_ADDRESS_MAX_LENGTH}
                        size='small'
                        changeOnBlur
                        qa={qa('shipping-to-editor')}
                    />
                </Grid>

                <Grid gap={16}>
                    <TextField
                        name='shipVia'
                        label={messages.shipViaLabel}
                        placeholder={messages.shipViaPlaceholder}
                        maxLength={constants.qBooksConstants.SHIP_VIA_MAX_LENGTH}
                        size='small'
                        changeOnBlur
                        qa={qa('ship-via-editor')}
                    />

                    <TextField
                        name='trackingNumber'
                        label={messages.trackingNumberLabel}
                        placeholder={messages.trackingNumberPlaceholder}
                        maxLength={constants.qBooksConstants.TRACKING_NUMBER_MAX_LENGTH}
                        size='small'
                        changeOnBlur
                        qa={qa('tracking-number-editor')}
                    />

                    <DatePicker
                        name='shippingDate'
                        label={messages.shippingDateLabel}
                        placeholder={messages.shippingDatePlaceholder}
                        size='small'
                        qa={qa('shipping-date-editor')}
                    />
                </Grid>

                <Grid gap={16}>
                    <TextField
                        name='shippingFrom'
                        label={messages.shippingFromLabel}
                        placeholder={messages.shippingFromPlaceholder}
                        maxLength={constants.qBooksConstants.SHIPPING_ADDRESS_MAX_LENGTH}
                        size='small'
                        changeOnBlur
                        qa={qa('shipping-from-editor')}
                    />

                    <NumberField
                        name='shippingPrice'
                        label={messages.shippingPriceLabel({ currency })}
                        placeholder={messages.shippingPricePlaceholder}
                        size='small'
                        qa={qa('shipping-price-editor')}
                    />

                    {!isUSOrg && (
                        <Select
                            name='shippingTaxCode'
                            items={taxRateItems}
                            itemNameKey='text'
                            label={messages.shippingTaxCodeLabel}
                            placeholder={messages.shippingTaxCodePlaceholder}
                            size='small'
                            qa={qa('shipping-tax-code-editor')}
                        />
                    )}
                </Grid>
            </Grid>
        </Form>
    );
});

SalesInvoiceShippingDetailsSection.displayName = 'SalesInvoiceShippingDetailsSection';
